import { useToast } from '@fsd/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { githubService } from '../services/github';
import type { ProductFormData } from '../types/product';
import { generateId } from '../utils/slug';

/**
 * Hook para salvar produtos no GitHub
 * Usa React Query para gerenciar estado e cache
 */
export function useSaveProduct() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async (data: ProductFormData) => {
      const productWithId = {
        ...data,
        id: data.id || generateId(),
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await githubService.saveProduct(productWithId);
      return productWithId;
    },
    onSuccess: (data) => {
      showSuccess('Produto salvo com sucesso!');
      // Invalida cache para refetch
      queryClient.invalidateQueries({ queryKey: ['products', data.category] });
    },
    onError: (error) => {
      console.error('Error saving product:', error);
      showError('Erro ao salvar produto. Verifique suas credenciais do GitHub.');
    },
  });
}

/**
 * Hook para listar produtos de uma categoria
 */
export function useProducts(category: string) {
  return useQuery({
    queryKey: ['products', category],
    queryFn: () => githubService.listProducts(category),
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
}

/**
 * Hook para deletar produtos
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async ({ category, slug }: { category: string; slug: string }) => {
      await githubService.deleteProduct(category, slug);
      return { category, slug };
    },
    onSuccess: (data) => {
      showSuccess('Produto deletado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['products', data.category] });
    },
    onError: (error) => {
      console.error('Error deleting product:', error);
      showError('Erro ao deletar produto. Tente novamente.');
    },
  });
}
